import axios from 'axios';
import { authService } from './authService';
import { env } from '@/config/env';
import { setAuth, readAuthFromStorage } from '@/store/authSlice';

// In dev: VITE_BASE_URL is empty → baseURL is '/api/' (proxied by Vite through same origin)
// In prod: VITE_BASE_URL is full URL → baseURL is 'https://api.example.com/api/'
const baseURL = env.VITE_BASE_URL
  ? `${env.VITE_BASE_URL.replace(/\/$/, "")}/api/`
  : '/api/';

const api = axios.create({
  baseURL,
  timeout: 300000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // Send HttpOnly cookie on every request automatically
});

type AppDispatchLike = (action: unknown) => unknown;
let storeDispatch: AppDispatchLike | null = null;

export const registerApiStoreDispatch = (dispatch: AppDispatchLike) => {
  storeDispatch = dispatch;
};

let isRefreshing = false;

type QueueItem = { resolve: () => void; reject: (err: any) => void; };
let failedQueue: QueueItem[] = [];

const processQueue = (error: any) => {
  failedQueue.forEach((p) => error ? p.reject(error) : p.resolve());
  failedQueue = [];
};

// Silently rotates the session using the HttpOnly refresh_token cookie.
// The API validates, revokes the old refresh token, and sets fresh jwt + refresh_token cookies.
// After updating localStorage, dispatches setAuth so the Redux store reflects the new role
// immediately — without requiring a second page refresh.
const refreshJwt = async () => {
  const token = await authService.refreshSession();

  // Update non-sensitive metadata in localStorage from the refreshed session
  localStorage.setItem("IsAdmin", String(token.isAdmin));
  if (token.role) {
    localStorage.setItem("Role", token.role);
    localStorage.setItem("TabPermissions", JSON.stringify(token.tabPermissions ?? {}));
  }

  // Sync Redux store so components react immediately without a second page reload
  storeDispatch?.(setAuth(readAuthFromStorage()));
};

// ----------------------
// Response Interceptor — handles 401 by refreshing the cookie
// ----------------------
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't retry if the refresh endpoint itself returned 401 — the refresh token is gone.
    const isRefreshEndpoint = originalRequest.url?.includes("auth/sessions/refresh");
    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshEndpoint) {
      if (isRefreshing) {
        return new Promise<void>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await refreshJwt();
        processQueue(null);
        return api(originalRequest); // Cookie is automatically re-sent
      } catch (err) {
        processQueue(err);
        // Refresh token is gone — clear session metadata and redirect to login
        localStorage.removeItem("IsAdmin");
        localStorage.removeItem("Role");
        localStorage.removeItem("TabPermissions");
        storeDispatch?.({ type: "auth/clearAuth" });
        window.location.href = "/";
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
